import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Building2, MapPin, DollarSign, Mail, Target, Briefcase, Users, Linkedin, Star, Globe, CheckCircle, Wand2, FileDown, Sparkles, MessageCircle, HelpCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------- Post-match investor briefing (Phase 1.5) ----------
function InvestorBriefingCard({ investorId, firmName }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Try loading a cached briefing without generating
        const res = await fetch(`${BACKEND_URL}/api/investors/${investorId}/briefing`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ force: false }),
        });
        // Only auto-populate if it was a cached hit (fast). Fresh generation is user-initiated.
        if (res.ok) {
          const d = await res.json();
          if (d.cached) setBriefing(d);
        }
      } catch (e) { /* silent */ }
      setLoading(false);
    })();
  }, [investorId]);

  const draft = async (force = false) => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/investors/${investorId}/briefing`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ force }),
      });
      if (res.ok) {
        const d = await res.json();
        setBriefing(d);
        toast.success(d.cached ? "Loaded from cache" : `Briefing drafted for ${firmName}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed");
      }
    } catch (e) { toast.error("Failed"); }
    setBusy(false);
  };

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/investors/${investorId}/briefing/pdf`, { credentials: "include" });
      if (!res.ok) { toast.error("PDF failed"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `briefing_${firmName.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF failed"); }
  };

  return (
    <Card className="border-teal-200 bg-gradient-to-br from-teal-50/60 to-white" data-testid="investor-briefing">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-teal-900">
              <Sparkles className="w-5 h-5" /> Approach angle · AI briefing
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">Tailored to THIS investor&apos;s thesis, portfolio and stage — grounded in your framing + prep answers.</p>
          </div>
          <div className="flex items-center gap-2">
            {briefing && (
              <Button variant="outline" size="sm" onClick={downloadPdf} data-testid="briefing-pdf-btn">
                <FileDown className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            )}
            <Button size="sm" onClick={() => draft(!!briefing)} disabled={busy || loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="briefing-draft-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Wand2 className="w-4 h-4 mr-1.5" />}
              {briefing ? "Refresh" : "Draft briefing"}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-teal-500" /></div>
        ) : !briefing ? (
          <div className="border rounded-lg border-dashed border-teal-300 py-6 px-4 text-center text-sm text-slate-600 bg-white/60" data-testid="briefing-empty">
            <p>No briefing yet.</p>
            <p className="text-xs text-slate-400 mt-1">Click &quot;Draft briefing&quot; to get the approach angle, opening line, top questions each way, and their likely reasons to pass.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white border border-teal-200 rounded-lg p-3" data-testid="briefing-angle">
              <p className="text-[10px] text-teal-700 uppercase tracking-wide font-medium mb-1">Approach angle</p>
              <p className="text-sm leading-relaxed text-slate-800">{briefing.approach_angle}</p>
            </div>
            {briefing.opening_line && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="briefing-opening">
                <p className="text-[10px] text-amber-700 uppercase tracking-wide font-medium mb-1">Opening line</p>
                <p className="text-sm italic text-slate-800">&ldquo;{briefing.opening_line}&rdquo;</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <BriefingList icon={<MessageCircle className="w-3.5 h-3.5 text-teal-600" />} title="Their lens" items={briefing.their_lens} testid="briefing-lens" />
              <BriefingList icon={<Star className="w-3.5 h-3.5 text-emerald-600" />} title="Your edge for them" items={briefing.your_edge} testid="briefing-edge" />
              <BriefingList icon={<HelpCircle className="w-3.5 h-3.5 text-slate-600" />} title="Questions they'll ask" items={briefing.questions_they_will_ask} testid="briefing-qta" />
              <BriefingList icon={<HelpCircle className="w-3.5 h-3.5 text-teal-600" />} title="Questions to ask them" items={briefing.questions_to_ask_them} testid="briefing-qask" />
              <BriefingList icon={<AlertTriangle className="w-3.5 h-3.5 text-red-500" />} title="Risks they'll see" items={briefing.risks_they_will_see} testid="briefing-risks" tone="red" />
              {briefing.meeting_ask && (
                <div className="bg-white border border-slate-200 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium mb-1 flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-teal-600" /> Meeting ask</p>
                  <p className="text-sm text-slate-800">{briefing.meeting_ask}</p>
                </div>
              )}
            </div>
            {briefing.cached && briefing.generated_at && (
              <p className="text-[10px] text-slate-400 italic">Cached from {new Date(briefing.generated_at).toLocaleDateString()}. Click Refresh for a fresh draft.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BriefingList({ icon, title, items, testid, tone }) {
  if (!items || !items.length) return null;
  return (
    <div className={`bg-white border rounded-lg p-3 ${tone === "red" ? "border-red-200" : "border-slate-200"}`} data-testid={testid}>
      <p className="text-[10px] text-slate-500 uppercase tracking-wide font-medium mb-2 flex items-center gap-1.5">
        {icon} {title}
      </p>
      <ul className="space-y-1.5 text-xs text-slate-800">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-1.5 leading-snug">
            <span className="text-slate-400 mt-0.5">•</span>
            <span className="flex-1">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InvestorDetailContent({ user }) {
  const { investorId } = useParams();
  const navigate = useNavigate();
  const [investor, setInvestor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const investorRes = await fetch(`${BACKEND_URL}/api/investors/${investorId}`, { credentials: "include" });
        if (investorRes.ok) setInvestor(await investorRes.json());
      } catch (error) {
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [investorId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (!investor) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="p-12 text-center">
          <p className="text-slate-500">Investor not found</p>
          <Button onClick={() => navigate("/investors")} className="mt-4">Back</Button>
        </Card>
      </div>
    );
  }

  const focusAreas = investor.focus_areas || [];
  const portfolioCompanies = investor.portfolio_companies || [];
  const stageFocus = investor.stage_focus || [];
  const contacts = investor.contacts || [];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate("/investors")} className="rounded-full" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center">
            <span className="text-white font-bold text-xl">{investor.name?.charAt(0)}</span>
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{investor.name}</h1>
              {investor.is_angel && <Badge variant="secondary">Angel Investor</Badge>}
              {investor.leads_rounds && <Badge className="bg-green-100 text-green-700">Leads Rounds</Badge>}
            </div>
            {investor.location && (
              <div className="flex items-center gap-1 text-slate-500">
                <MapPin className="w-4 h-4" />
                <span>{investor.location}</span>
              </div>
            )}
          </div>
        </div>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Briefing (Phase 1.5 — post-match approach angle) */}
            <InvestorBriefingCard investorId={investor.investor_id || investor.firm_id} firmName={investor.name || "investor"} />

            {/* Investment Thesis */}
            {investor.investment_thesis && (
              <Card className="border border-slate-200">
                <CardHeader><CardTitle>Investment Thesis</CardTitle></CardHeader>
                <CardContent><p className="text-slate-600">{investor.investment_thesis}</p></CardContent>
              </Card>
            )}

            {/* Focus Areas */}
            {focusAreas.length > 0 && (
              <Card className="border border-slate-200">
                <CardHeader><CardTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-teal-600" />Focus Areas</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {focusAreas.map((area, i) => <Badge key={i} className="bg-teal-50 text-teal-700">{area}</Badge>)}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Portfolio */}
            {portfolioCompanies.length > 0 && (
              <Card className="border border-slate-200">
                <CardHeader><CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" />Portfolio Companies</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {portfolioCompanies.map((c, i) => <div key={i} className="p-3 bg-slate-50 rounded-lg text-center text-sm font-medium">{c}</div>)}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team / Contacts */}
            {contacts.length > 0 && (
              <Card className="border border-slate-200">
                <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" />Team ({contacts.length})</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {contacts.map((contact) => (
                      <div key={contact.contact_id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-slate-600 font-medium">{contact.first_name?.charAt(0)}{contact.last_name?.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-slate-900">{contact.first_name} {contact.last_name}</h4>
                            {contact.is_primary_contact && (
                              <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                                <Star className="w-3 h-3 mr-1 fill-yellow-500" />Primary
                              </Badge>
                            )}
                          </div>
                          {contact.role && <p className="text-sm text-slate-600">{contact.role}</p>}
                          
                          {/* Contact Intel */}
                          {contact.intel && (
                            <p className="text-sm text-slate-500 mt-2 italic">&ldquo;{contact.intel}&rdquo;</p>
                          )}
                          
                          {/* Board Seats */}
                          {contact.board_seats?.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs text-slate-500">Board seats: </span>
                              <span className="text-xs text-slate-700">{contact.board_seats.join(", ")}</span>
                            </div>
                          )}

                          {/* Contact Links */}
                          <div className="flex gap-3 mt-3">
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1">
                                <Mail className="w-4 h-4" />Email
                              </a>
                            )}
                            {contact.linkedin_url && (
                              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                <Linkedin className="w-4 h-4" />LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <Card className="border border-slate-200">
              <CardContent className="p-6 space-y-4">
                {(investor.min_ticket_size || investor.max_ticket_size) && (
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="text-sm text-slate-500">Check Size</p>
                      <p className="font-semibold">{investor.min_ticket_size || "?"} - {investor.max_ticket_size || "?"}</p>
                    </div>
                  </div>
                )}
                
                {stageFocus.length > 0 && (
                  <div className="flex items-center gap-3">
                    <Target className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-slate-500">Stage Focus</p>
                      <p className="font-semibold">{stageFocus.join(", ")}</p>
                    </div>
                  </div>
                )}

                {investor.leads_rounds && (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm text-slate-500">Round Leadership</p>
                      <p className="font-semibold text-green-700">Can Lead Rounds</p>
                    </div>
                  </div>
                )}

                {investor.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Website</p>
                      <a href={investor.website} target="_blank" rel="noopener noreferrer" className="font-medium text-teal-600 hover:text-teal-700 text-sm truncate block">
                        {investor.website.replace(/^https?:\/\//, "")}
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA */}
            <Card className="border border-teal-200 bg-teal-50/50">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-slate-600 mb-4">Want to match with this investor?</p>
                <Button onClick={() => navigate("/strategy")} className="btn-secondary w-full" data-testid="upload-deck-cta">Complete Your Strategy</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
}

export default function InvestorDetail() {
  return (
    <AppLayout>
      {({ user }) => <InvestorDetailContent user={user} />}
    </AppLayout>
  );
}
